import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { LogOut, Menu, X } from 'lucide-react';
import { Tooltip } from '../ui/Tooltip';
import './Header.scss';

export const Header: React.FC = () => {
  const { user, isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/');
    setIsMobileMenuOpen(false);
  };

  const isActiveRoute = (path: string) => {
    return (
      location.pathname === path || location.pathname.startsWith(path + '/')
    );
  };

  const getUserInitials = () => {
    if (!user?.firstName && !user?.lastName) return 'U';
    return `${user?.firstName?.[0] || ''}${user?.lastName?.[0] || ''}`.toUpperCase();
  };

  // Debug: Log user data
  useEffect(() => {
    if (user) {
      console.log('Header user data:', {
        firstName: user.firstName,
        lastName: user.lastName,
        email: user.email,
        subscription: user.subscription,
      });
    }
  }, [user]);

  const isGeneratePage = location.pathname === '/generate';

  return (
    <>
      <header
        className={`app-header ${isScrolled ? 'scrolled' : ''} ${isGeneratePage ? 'generate-page-header' : ''}`}
      >
        <div className="header-container">
          <div className="header-content">
            <div className="header-left">
              <Link to={isAuthenticated ? '/dashboard' : '/'} className="logo">
                <div className="logo-icon">
                  <div className="icon-layers">
                    <div className="layer-back"></div>
                    <div className="layer-middle"></div>
                    <div className="layer-front"></div>
                  </div>
                  <div className="icon-spark">✦</div>
                </div>
                <span className="logo-text">Odyssey</span>
              </Link>

              <nav className="main-nav">
                {isAuthenticated && (
                  <Link
                    to="/dashboard"
                    className={`nav-link ${isActiveRoute('/dashboard') ? 'active' : ''}`}
                  >
                    Dashboard
                  </Link>
                )}
                {isAuthenticated && (
                  <Link
                    to="/my-courses"
                    className={`nav-link ${isActiveRoute('/my-courses') ? 'active' : ''}`}
                  >
                    My Courses
                  </Link>
                )}
                <Link
                  to={isAuthenticated ? '/browse' : '/explore'}
                  className={`nav-link ${isActiveRoute('/browse') || isActiveRoute('/explore') ? 'active' : ''}`}
                >
                  {isAuthenticated ? 'Browse Courses' : 'Browse Courses'}
                </Link>
                <Link
                  to={isAuthenticated ? '/generate' : '/try-generate'}
                  className={`nav-link ${isActiveRoute('/generate') || isActiveRoute('/try-generate') ? 'active' : ''}`}
                >
                  Generate
                </Link>
                {!isAuthenticated && (
                  <Link
                    to="/pricing"
                    className={`nav-link ${isActiveRoute('/pricing') ? 'active' : ''}`}
                  >
                    Pricing
                  </Link>
                )}
              </nav>
            </div>

            <div className="header-right">
              {isAuthenticated ? (
                <div className="user-section">
                  <div
                    className="user-info clickable"
                    onClick={() => navigate('/profile')}
                    role="button"
                    tabIndex={0}
                    onKeyDown={e => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        navigate('/profile');
                      }
                    }}
                  >
                    <div className="user-avatar">{getUserInitials()}</div>
                    <span className="user-name">
                      {user?.firstName || user?.email?.split('@')[0] || 'User'}
                    </span>
                  </div>
                  <div className="user-actions">
                    <Tooltip
                      content="Sign out of your account"
                      position="bottom"
                    >
                      <button
                        className="action-button logout-button"
                        onClick={handleLogout}
                      >
                        Logout
                      </button>
                    </Tooltip>
                  </div>
                </div>
              ) : (
                <div className="auth-buttons">
                  <button
                    className="login-button"
                    onClick={() => navigate('/login')}
                  >
                    Login
                  </button>
                  <button
                    className="signup-button"
                    onClick={() => navigate('/register')}
                  >
                    Sign Up
                  </button>
                </div>
              )}

              <button
                className="mobile-menu-button"
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              >
                {isMobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="mobile-menu">
          <nav className="mobile-nav">
            {isAuthenticated && (
              <Link
                to="/dashboard"
                className={`mobile-nav-link ${isActiveRoute('/dashboard') ? 'active' : ''}`}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Dashboard
              </Link>
            )}
            {isAuthenticated && (
              <Link
                to="/my-courses"
                className={`mobile-nav-link ${isActiveRoute('/my-courses') ? 'active' : ''}`}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                My Courses
              </Link>
            )}
            <Link
              to={isAuthenticated ? '/browse' : '/explore'}
              className={`mobile-nav-link ${isActiveRoute('/browse') || isActiveRoute('/explore') ? 'active' : ''}`}
              onClick={() => setIsMobileMenuOpen(false)}
            >
              {isAuthenticated ? 'Browse Courses' : 'Browse Courses'}
            </Link>
            {!isAuthenticated && (
              <Link
                to="/pricing"
                className={`mobile-nav-link ${isActiveRoute('/pricing') ? 'active' : ''}`}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Pricing
              </Link>
            )}
            {isAuthenticated && (
              <Link
                to="/generate"
                className={`mobile-nav-link ${isActiveRoute('/generate') ? 'active' : ''}`}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Generate
              </Link>
            )}
            {isAuthenticated && (
              <Link
                to="/profile"
                className={`mobile-nav-link ${isActiveRoute('/profile') ? 'active' : ''}`}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Profile
              </Link>
            )}
            {isAuthenticated && (
              <button
                className="mobile-nav-link logout-link"
                onClick={handleLogout}
              >
                Logout
              </button>
            )}
          </nav>
        </div>
      )}
    </>
  );
};
